import { AskRequest } from '@hone-automation/common';
import { getShortId } from '../../../utils/shortId';

/**
 * Parse amount operator from search query
 * Supports: >100, <100, >=100, <=100, 100 (exact), 50-100 (range), -500--400 (negative range)
 */
interface AmountFilter {
  operator: '>' | '<' | '>=' | '<=' | '=' | 'range';
  value: number;
  rangeEnd?: number;
}

/**
 * Parse an amount filter from a query string
 * Returns null if no amount filter found
 */
export const parseAmountFilter = (query: string): { filter: AmountFilter; remainingQuery: string } | null => {
  // Match patterns like: >100, <100, >=100, <=100, 100, 50-100, -500--400
  // Pattern should be at the start or after a space
  const patterns = [
    // Range with negative end: -500--400 or 100--50 (double dash means negative end)
    { regex: /(?:^|\s)(-?[\d,.]+)--(-?[\d,.]+)(?:\s|$)/i, type: 'range' as const },
    // Range: 50-100 or -50-100 (positive end only)
    { regex: /(?:^|\s)(-?[\d,.]+)\s*-\s*([\d,.]+)(?:\s|$)/i, type: 'range' as const },
    // Operators with optional negative: >=, <=, >, < followed by optional negative number
    { regex: /(?:^|\s)(>=|<=|>|<)\s*(-?[\d,.]+)(?:\s|$)/i, type: 'operator' as const },
    // Exact amount with $ prefix: $100 or $-100
    { regex: /(?:^|\s)\$(-?[\d,.]+)(?:\s|$)/i, type: 'exact' as const },
    // Plain number (exact amount): 100, -100, or 1,234.56
    { regex: /^(-?[\d,.]+)$/, type: 'exact' as const },
  ];

  for (const { regex, type } of patterns) {
    const match = query.match(regex);
    if (match) {
      const fullMatch = match[0];
      const remainingQuery = query.replace(fullMatch, ' ').trim();

      if (type === 'range') {
        const start = parseFloat(match[1].replace(/,/g, ''));
        const end = parseFloat(match[2].replace(/,/g, ''));
        if (!isNaN(start) && !isNaN(end)) {
          return {
            filter: { operator: 'range', value: Math.min(start, end), rangeEnd: Math.max(start, end) },
            remainingQuery,
          };
        }
      } else if (type === 'operator') {
        const op = match[1] as '>' | '<' | '>=' | '<=';
        const value = parseFloat(match[2].replace(/,/g, ''));
        if (!isNaN(value)) {
          return { filter: { operator: op, value }, remainingQuery };
        }
      } else if (type === 'exact') {
        const value = parseFloat(match[1].replace(/,/g, ''));
        if (!isNaN(value)) {
          return { filter: { operator: '=', value }, remainingQuery };
        }
      }
    }
  }

  return null;
};

/**
 * Check if an amount matches the filter
 */
export const matchesAmountFilter = (amount: number | undefined | null, filter: AmountFilter): boolean => {
  if (amount === undefined || amount === null) return false;

  switch (filter.operator) {
    case '>':
      return amount > filter.value;
    case '<':
      return amount < filter.value;
    case '>=':
      return amount >= filter.value;
    case '<=':
      return amount <= filter.value;
    case '=':
      // Allow small tolerance for floating point comparison
      return Math.abs(amount - filter.value) < 0.01;
    case 'range':
      return amount >= filter.value && amount <= (filter.rangeEnd ?? filter.value);
    default:
      return false;
  }
};

/**
 * Get the amount from a request (flaggedAmount or transaction.amount)
 */
const getRequestAmount = (request: AskRequest): number | null => {
  // @ts-ignore - flaggedAmount may exist
  if (request?.flaggedAmount) {
    const parsed = parseFloat(String(request.flaggedAmount));
    return isNaN(parsed) ? null : parsed;
  }
  // @ts-ignore - transaction.amount may exist
  if (request.transaction?.amount) {
    return request.transaction.amount;
  }
  return null;
};

/**
 * Check if a request matches a text query
 * Searches: identifier, comments (message content), transaction ID
 */
const matchesTextQuery = (request: AskRequest, query: string): boolean => {
  if (!query) return true;

  const lowerQuery = query.toLowerCase();

  // Check for short ID search with # prefix (e.g., "#1844")
  const shortIdMatch = lowerQuery.match(/^#(\d+)$/);
  if (shortIdMatch && request.id) {
    const searchShortId = shortIdMatch[1];
    const requestShortId = getShortId(request.id);
    return requestShortId === searchShortId || requestShortId.endsWith(searchShortId);
  }

  const searchTerms = lowerQuery.split(/\s+/).filter(Boolean);

  // If no search terms, match all
  if (searchTerms.length === 0) return true;

  // Build searchable text from request
  const searchableFields: string[] = [];

  // Identifier/title
  if (request.identifier) {
    searchableFields.push(request.identifier.toLowerCase());
  }

  // Request ID (full UUID)
  if (request.id) {
    searchableFields.push(request.id.toLowerCase());
    // Also add short ID for searching without # prefix
    searchableFields.push(getShortId(request.id));
  }

  // Comments - search message content
  if (request.comments && Array.isArray(request.comments)) {
    for (const comment of request.comments) {
      // @ts-ignore - comment field may vary
      const commentText = comment?.comment || comment?.message || comment?.text;
      if (commentText && typeof commentText === 'string') {
        searchableFields.push(commentText.toLowerCase());
      }
      // Also search author name/email
      // @ts-ignore
      const authorName = comment?.user?.name || comment?.userLocation?.username || comment?.userLocation?.email;
      if (authorName && typeof authorName === 'string') {
        searchableFields.push(authorName.toLowerCase());
      }
    }
  }

  // Transaction ID
  // @ts-ignore - transaction fields may exist
  if (request.transaction?.id) {
    searchableFields.push(request.transaction.id.toLowerCase());
  }

  // Combine all searchable text
  const searchableText = searchableFields.join(' ');

  // All search terms must match (AND logic)
  return searchTerms.every(term => searchableText.includes(term));
};

/**
 * Extract just the text portion of a search query (without amount operators)
 * Useful for highlighting text matches in the UI
 */
export const getTextQuery = (query: string): string => {
  const trimmed = query.trim();
  if (!trimmed) return '';

  const amountResult = parseAmountFilter(trimmed);
  return amountResult?.remainingQuery ?? trimmed;
};

/**
 * Filter requests by search query
 * Supports text search and amount operators
 */
export const filterBySearch = (requests: AskRequest[], query: string): AskRequest[] => {
  const trimmedQuery = query.trim();

  // No query = return all
  if (!trimmedQuery) return requests;

  // Parse amount filter if present
  const amountResult = parseAmountFilter(trimmedQuery);
  const amountFilter = amountResult?.filter ?? null;
  const textQuery = amountResult?.remainingQuery ?? trimmedQuery;

  return requests.filter(request => {
    // Check amount filter
    if (amountFilter) {
      const amount = getRequestAmount(request);
      if (!matchesAmountFilter(amount, amountFilter)) {
        return false;
      }
    }

    // Check text query
    if (textQuery && !matchesTextQuery(request, textQuery)) {
      return false;
    }

    return true;
  });
};
